import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, CalendarCheck, Trophy, Sparkles, Phone, Mail, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import GalleryManager from "@/pages/GalleryManager";

const STATUSES = ["new", "contacted", "booked", "won", "archived"];
const STATUS_STYLES = {
  new: "bg-gold/20 text-gold border-gold/40",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  booked: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  won: "bg-green-500/15 text-green-300 border-green-500/40",
  archived: "bg-white/10 text-white/50 border-white/20",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, booked: 0, won: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l._id === id ? { ...l, status } : l)));
      const s = await api.get("/leads/stats");
      setStats(s.data);
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  const CARDS = [
    { label: "Total Leads", value: stats.total, icon: Inbox },
    { label: "New", value: stats.new, icon: Sparkles },
    { label: "Booked", value: stats.booked, icon: CalendarCheck },
    { label: "Won", value: stats.won, icon: Trophy },
  ];

  return (
    <div className="min-h-screen flex bg-ivory">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 velvet-deep text-ivory p-6 shrink-0">
        <img src="/logo-monogram.png" alt="West Ouachita Cabinets" className="h-24 w-auto mx-auto rounded-[2px] ring-1 ring-gold/30" />
        <p className="text-center font-serif text-lg mt-3">Leads Dashboard</p>
        <div className="gold-hairline my-6" />
        <p className="text-[#66768c] text-xs tracking-luxe uppercase">Signed in as</p>
        <p className="text-ivory text-sm mt-1 truncate">{user?.email}</p>
        <button
          data-testid="admin-logout"
          onClick={doLogout}
          className="mt-auto flex items-center gap-2 text-[#A1B0C4] hover:text-gold transition-colors text-sm"
        >
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-navy/10 px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl text-navy">Consultation Requests</h1>
            <p className="text-navy/50 text-sm">Follow up fast. Leads never sit long.</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="flex items-center gap-2 text-navy/60 hover:text-navy text-sm border border-navy/15 px-4 py-2">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button data-testid="admin-logout-mobile" onClick={doLogout} className="md:hidden text-navy/60"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>

        <div className="p-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
            {CARDS.map((c) => (
              <div key={c.label} className="bg-white border border-navy/10 p-6">
                <div className="flex items-center justify-between">
                  <p className="text-navy/50 text-xs tracking-luxe uppercase">{c.label}</p>
                  <c.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                </div>
                <p className="font-serif text-4xl text-navy mt-3">{c.value}</p>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white border border-navy/10 mt-6 overflow-x-auto" data-testid="leads-table">
            {loading ? (
              <p className="p-10 text-center text-navy/50">Loading leads…</p>
            ) : leads.length === 0 ? (
              <div className="p-16 text-center">
                <Inbox className="h-10 w-10 text-navy/20 mx-auto" strokeWidth={1} />
                <p className="text-navy/50 mt-3">No leads yet. New consultation requests will appear here.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-navy/10 hover:bg-transparent">
                    <TableHead className="text-navy/60 uppercase text-xs tracking-wider">Contact</TableHead>
                    <TableHead className="text-navy/60 uppercase text-xs tracking-wider">Project</TableHead>
                    <TableHead className="text-navy/60 uppercase text-xs tracking-wider">Source</TableHead>
                    <TableHead className="text-navy/60 uppercase text-xs tracking-wider">Received</TableHead>
                    <TableHead className="text-navy/60 uppercase text-xs tracking-wider">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l._id} className="border-navy/10" data-testid={`lead-row-${l._id}`}>
                      <TableCell className="align-top">
                        <p className="font-medium text-navy">{l.name}</p>
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-navy/60 text-sm mt-1 hover:text-gold">
                          <Phone className="h-3.5 w-3.5" /> {l.phone}
                        </a>
                        <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-navy/60 text-sm mt-0.5 hover:text-gold">
                          <Mail className="h-3.5 w-3.5" /> {l.email}
                        </a>
                        {l.message ? <p className="text-navy/50 text-sm mt-2 max-w-xs italic">"{l.message}"</p> : null}
                      </TableCell>
                      <TableCell className="align-top text-navy/80">{l.project_type}</TableCell>
                      <TableCell className="align-top text-navy/60 text-sm">{l.referral_source || "—"}</TableCell>
                      <TableCell className="align-top text-navy/60 text-sm whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                      <TableCell className="align-top">
                        <Select value={l.status} onValueChange={(v) => changeStatus(l._id, v)}>
                          <SelectTrigger data-testid={`status-select-${l._id}`} className={`w-[130px] rounded-none border text-xs uppercase tracking-wider ${STATUS_STYLES[l.status] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs uppercase tracking-wider">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <GalleryManager />
        </div>
      </div>
    </div>
  );
}
